import { Tooltip } from "@progress/kendo-react-tooltip";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { setEmailGridData, setEmailType, setSelectedEmailData, setShowEmailDescPopup, setUnreadEmailCount, toggleDescPopup } from "../../../../actions/taskSidebar";
import { dateFormat, icon, label, notifyIcon, number, quote, tooltip } from "../../../../config";
import { convertToTimeZone } from "../../../../helper/common";
import { formatInTimeZone, utcToZonedTime } from "date-fns-tz";
import {
  addSpamDetails,
  deleteEmail,
  deleteEmailDraft,
  deleteEmailReply,
  getAllEmailsForTask,
  getWorkflowEmails,
  updateEmailDetails,
  updateEmailReply,
} from "../../../../shared/services/workflowEmails.service";
import store from "../../../../store";
import { getNotification } from "../../../../utils/common";
import { GridContext } from "../../../../utils/kendo";
import EmailPopUpSidebar from "./EmailPopUpSidebar";
import { email_icon, email_label } from "./TaskEmailConstant";

/**
 * Component to display the creation date of an email
 * @param {Object} props - Component props
 * @param {Object} props.dataItem - Email data item
 * @author Sejal
 */
export const CreatedOn = (props) => {
  const { dataItem } = props;
  const userTimezone = useSelector((state) => state.auth.user?.timezone) || label.UTC;
  const createdDate = dataItem?.CreatedOn ? dataItem.CreatedOn : null;
  let formattedDate = "";
  
  if (createdDate) {
    const dateObj = new Date(createdDate);
    const zonedDate = utcToZonedTime(dateObj, userTimezone);
    const todayInUserTz = utcToZonedTime(new Date(), userTimezone);
    const zonedDateStr = formatInTimeZone(zonedDate, userTimezone,  dateFormat?.REMINDER_DATE_FORMAT);
    const todayStr = formatInTimeZone(todayInUserTz, userTimezone, dateFormat?.REMINDER_DATE_FORMAT);
    const isToday = zonedDateStr === todayStr;
    
    if (isToday) {
      formattedDate = convertToTimeZone(createdDate, dateFormat?.TODAY_DATE_FORMAT);
    } else {
      formattedDate = convertToTimeZone(createdDate, dateFormat?.MONTH_DATE_FORMAT);
    }
  }

  return (
    <td>
      <span className='cursor-default text-size'>{formattedDate}</span>
    </td>
  );
};

/**
 * Component to display the subject and partial content of an email
 * @param {Object} props - Component props
 * @param {Object} props.dataItem - Email data item
 * @author Sejal
 */
export const Subject = (props) => {
  const { dataItem } = props;
  const gridContext = useContext(GridContext);
  const { gridData, setGridData } = gridContext;
  const { Subject, PartialContent } = dataItem;

  // Function to clean HTML content
  const cleanContent = (content) => {
    if (!content) return "";
    // Create a temporary div to parse HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;
    // Get text content, which automatically removes HTML tags
    let cleaned = tempDiv.textContent || tempDiv.innerText;
    // Replace multiple spaces and newlines with single space
    cleaned = cleaned.replace(/\s+/g, " ");
    // Trim leading/trailing spaces
    return cleaned.trim();
  };
  return (
    <td>
      <span className='single-line cursor-default text-size'>
        <span>{Subject}</span>
        <span className='partial-content cursor-default'>{cleanContent(PartialContent)}</span>
      </span>
    </td>
  );
};

/**
 * Component to display the sender name of an email
 * @param {Object} props - Component props
 * @param {Object} props.dataItem - Email data item
 * @author Sejal
 */
export const Sender = (props) => {
  const { dataItem } = props;
  const gridContext = useContext(GridContext);
  const { SenderName } = dataItem;

  return (
    <td>
      <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
        <div className='cursor-default text-truncate text-size' title={SenderName}>
          {SenderName}
        </div>
      </Tooltip>
    </td>
  );
};

/**
 * Component to display the reply status of an email
 * @param {Object} props - Component props
 * @param {Object} props.dataItem - Email data item
 * @author Sejal
 */
export const Reply = (props) => {
  const { dataItem } = props;
  const { user } = useSelector((state) => state.auth);
  const gridContext = useContext(GridContext);
  const iconClassName = dataItem?.Flag === number.ONE ? "material-symbols-fill" : "grey";
  const { selectedMode, gridData, setGridData, hover } = gridContext;
  const dispatch = useDispatch();
  const showAttachFile = dataItem?.attachmentCount > number.ZERO;

  /**
   * Flag the email
   * @param {Event} e - The click event
   * @author Sejal
   */
  const flagEmail = async (e) => {
    e.stopPropagation();
    const payload = {
      emailDetails: {
        emailThreadId: dataItem?.id,
        flag: dataItem?.Flag === number.ONE ? number.ZERO : number.ONE,
        emailRefType: dataItem?.EmailType,
      },
      userId: user?.id,
    };
    if (dataItem?.EmailType === email_label.OUTGOING_EMAIL_CONSTANT || dataItem?.EmailType === email_label.COMPOSE_EMAIL_CONSTANT) {
      await dispatch(updateEmailReply(payload));
    } else {
      await dispatch(updateEmailDetails(payload));
    }
    const updatedGridData = gridData.map((item) =>
      item?.id === dataItem?.id ? { ...item, Flag: item?.Flag === number.ONE ? number.ZERO : number.ONE } : item
    );
    dispatch(setEmailGridData(updatedGridData));
  };

  return (
    <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
      <td className='d-flex align-items-center'>
        <span
          title={
            dataItem?.EmailType === email_label.OUTGOING_EMAIL_CONSTANT || dataItem?.EmailType === email_label.COMPOSE_EMAIL_CONSTANT
              ? email_label.OUTGOING_EMAIL
              : dataItem?.EmailType === email_label.DRAFT_CONSTANT
              ? email_label.DRAFT_MAIL
              : email_label.INCOMING_EMAIL
          }
          className={` ${
            dataItem?.EmailType === email_label.OUTGOING_EMAIL_CONSTANT || dataItem?.EmailType === email_label.COMPOSE_EMAIL_CONSTANT
              ? "icon-style-outgoing"
              : dataItem?.EmailType === email_label.DRAFT_CONSTANT
              ? "grey"
              : "icon-style-incoming"
          } cursor-default`}>
          {dataItem?.EmailType === email_label.DRAFT_CONSTANT ? icon.DRAFTS_EMAILS : email_icon.OUTGOING_EMAIL}
        </span>
        <span className={` ${showAttachFile ? "" : "hide"} grey`} title={label.ATTACHMENT}>
          {icon.ATTACH_FILE}
        </span>
        {dataItem?.EmailType !== email_label.DRAFT_CONSTANT &&
          (dataItem?.Flag === number.ONE ? (
            <span
              className={`material-symbols-fill  filled-icon cursor-pointer email-action-flag-icon grey `}
              id='team-email-flag-btn'
              title={tooltip.UNFLAG}
              onClick={flagEmail}>
              {icon.FLAG_2}
            </span>
          ) : (
            <span
              className={`${dataItem?.id === hover?.id ? "show" : "hide"} ${iconClassName} cursor-pointer email-action-flag-icon grey `}
              id='mark-team-email-spam-btn'
              title={tooltip.FLAG}
              onClick={flagEmail}>
              {icon.FLAG_2}
            </span>
          ))}
      </td>
    </Tooltip>
  );
};

/**
 * Custom hook to manage email fetching and popup state
 *
 */
export const useTeamEmailTaskSidebar = ({
  task,
  selectedEmailId,
  setSelectedEmailData,
  setGridData,
  offset,
  setSelectedEmailId,
  setHover,
  setShowEmailDescPopup,
  setOffset,
  hover,
  setCurrentEmailData,
  currentEmailData,
  resetScroller,
}) => {
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const { myProjectId } = useSelector((state) => state.auth.user);
  const { user } = useSelector((state) => state.auth);
  const { lastSelectedBoard } = useSelector((state) => state.auth.user);
  const { emailGridData } = useSelector((state) => state.taskSidebar);

  /**
   * Calls the fetch function when component is rendered or task changes
   * @author Sejal
   */
  useEffect(() => {
    fetchEmailData();
  }, [task]);

  /**
   *  fetch all the emails for the task
   * @author sejal
   */
  const fetchEmailData = async () => {
    if (task?.taskId) {
      const emailData = await dispatch(
        getAllEmailsForTask({
          limit: number.FIVE,
          offset: number.ZERO,
          taskId: task.taskId,
        })
      );

      if (emailData) {
        dispatch(setEmailGridData(emailData));
        setCurrentEmailData(emailData?.length);
      } else {
        dispatch(setEmailGridData([]));
        setCurrentEmailData(0);
      }
    } else {
      dispatch(setEmailGridData([]));
      setCurrentEmailData(0);
    }
  };

  /**
   * scroll handler to implement load on demand
   */
  const scrollHandler = useCallback(
    async (event) => {
      const e = event.nativeEvent;
      const scrollThreshold = 50; // Increased threshold for reliable scroll detection
      const isNearBottom = e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight <= scrollThreshold;
      if (isNearBottom) {
        let res;
        setOffset(offset + number.FIVE);
        if (currentEmailData && task?.taskId) {
          const payload = {
            limit: number.FIVE,
            offset: offset + number.FIVE,
            taskId: task.taskId,
          };
          res = await dispatch(getAllEmailsForTask(payload));
          if (res) {
            setCurrentEmailData(res?.length);
            if (res?.length) {
              const updatedData = [...emailGridData, ...res];
              dispatch(setEmailGridData(updatedData));
            }
          } else {
            getNotification(res?.message, notifyIcon.WARNING_ICON);
          }
        }
      }
    },
    [offset, currentEmailData, task?.id, emailGridData]
  );

  /**
   * returns custom row of grid
   * @param {*} tr
   * @param {*} props
   */
  const customRowRender = (tr, props) => {
    return (
      <RowRender
        originalProps={props}
        tr={tr}
        hover={hover}
        setHover={setHover}
        exitEdit={() => {}}
        setSelectedEmailId={setSelectedEmailId}
        selectedEmailId={selectedEmailId}
        setShowEmailDescPopup={setShowEmailDescPopup}
        setSelectedEmailData={setSelectedEmailData}
      />
    );
  };

  return {
    customRowRender,
    scrollHandler,
  };
};

/**
 * Component to handle email actions like flag, reply, and more info
 * @param {Object} props - Component props
 * @param {Object} props.dataItem - Email data item
 * @author Sejal
 */
export const emailAction = ({ dataItem, setSelectedEmailId }) => {
  const { attachmentCount, EmailType } = dataItem;
  const gridContext = useContext(GridContext);
  const { gridData, setGridData, hover } = gridContext;
  const showAttachFile = attachmentCount > number.ZERO;
  const { emailData, task } = useSelector((state) => state.taskSidebar);
  const { user } = useSelector((state) => state.auth);
  const { emailFilter } = useSelector((state) => state.teamFilter);
  const dispatch = useDispatch();
  const anchor = React.useRef(null);
  const [showLinkPopUp, setShowLinkPopUp] = useState(false);
  const isRead = dataItem.IsRead === 1;

  /**
   * Mark email as spam
   * @param {Event} e - The click event
   * @author Sejal
   */
  const handleMarkAsSpam = async (e) => {
    e?.stopPropagation();
    const emailArray = Array.isArray(dataItem) ? dataItem : [dataItem];
    const payload = {
      emailDetails: emailArray.map((item) => ({
        emailId: item?.Sender,
        projectId: item.ProjectId,
        companyId: null,
        emailThreadId: item.id,
      })),
      userId: user?.id,
    };
    const spamDetail = await dispatch(addSpamDetails(payload));
    if (spamDetail) {
      getNotification(quote.EMAIL_MARKED_AS_SPAM, notifyIcon.SUCCESS_ICON);
      const markedEmails = gridData.filter((item) => !emailArray.some((email) => email.id === item.id));
      dispatch(setEmailGridData(markedEmails));
    }
  };

  /**
   * Delete email(s)
   * @param {Event} e - The click event
   * @author Sejal
   */
  const handleDeleteEmails = async (e) => {
    e.stopPropagation();
    const emailArray = Array.isArray(dataItem) ? dataItem : [dataItem];
    const payload = {
      emailDetails: emailArray.map((item) => ({
        emailThreadId: item?.id,
        emailRefType: item?.EmailType,
      })),
    };
    let deleteEmailResult;
    if (dataItem?.EmailType === email_label.OUTGOING_EMAIL_CONSTANT || dataItem?.EmailType === email_label.COMPOSE_EMAIL_CONSTANT) {
      deleteEmailResult = deleteEmailReply(payload);
    } else if (dataItem?.EmailType === email_label.DRAFT_CONSTANT) {
      const payload = { emailDetails: { emailDraftId: dataItem?.id } };
      deleteEmailResult = deleteEmailDraft(payload);
    } else {
      deleteEmailResult = deleteEmail(payload);
    }
    if (deleteEmailResult) {
      let successLabel = "";
      successLabel =
        dataItem?.EmailType === email_label.DRAFT_CONSTANT
          ? label.DRAFT_DISCARDED
          : emailArray.length > number.ONE
          ? label.EMAILS_DELETED_SUCCESSFULLY
          : label.EMAIL_DELETED_SUCCESSFULLY;
      getNotification(successLabel, notifyIcon.SUCCESS_ICON);
      const remainingEmails = gridData.filter((item) => !emailArray.some((email) => email.id === item.id));
      dispatch(setEmailGridData(remainingEmails));
      const isSelectedEmailDeleted = emailArray.some((email) => email.id === emailData?.id);
      if (isSelectedEmailDeleted) {
        dispatch(setShowEmailDescPopup(false));
        dispatch(setSelectedEmailData(null));
      }
    } else {
      getNotification(label.ERROR_OCCURED, notifyIcon.ERROR_ICON);
    }
  };

  /**
   * Mark email as read/unread
   * @author Muskan Thakur
   */
  const handleMarkAsReadUnread = async (e) => {
    e.stopPropagation();
    const emailArray = Array.isArray(dataItem) ? dataItem : [dataItem];
    const newReadStatus = isRead ? number.ZERO : number.ONE;
    const payload = {
      emailDetails: emailArray.map((item) => ({
        emailThreadId: item?.id,
        isRead: newReadStatus,
      })),
      userId: user?.id,
    };
    const readDetail = await dispatch(updateEmailDetails(payload));
    if (readDetail) {
      const markedEmails =
        emailFilter === "isRead" && newReadStatus === number.ONE
          ? gridData.filter((item) => !emailArray.some((email) => email.id === item.id))
          : gridData.map((item) => (emailArray.some((email) => email.id === item.id) ? { ...item, IsRead: newReadStatus } : item));
      dispatch(setEmailGridData(markedEmails));
      
      // Update unread email count based on status change
      const currentUnreadCount = task?.unreadEmailCount || 0;
      let countChange = 0;
      
      // Count how many emails changed status (excluding outgoing and draft emails)
      emailArray.forEach((item) => {
          if (newReadStatus === number.ONE && item?.IsRead !== number.ONE) {
            // Marking as read: decrement count
            countChange -= 1;
          } else if (newReadStatus === number.ZERO && item?.IsRead === number.ONE) {
            // Marking as unread: increment count
            countChange += 1;
          }
      });
      
      if (countChange !== 0) {
        const newUnreadCount = Math.max(0, currentUnreadCount + countChange);
        dispatch(setUnreadEmailCount(newUnreadCount));
      }
    } else {
      getNotification(label.ERROR_OCCURED, notifyIcon.ERROR_ICON);
    }
  };

  /**
   * Show more info popup
   * @param {Event} e - The click event
   * @author Sejal
   */
  const handleMoreInfo = (e) => {
    e.stopPropagation();
    setShowLinkPopUp(true);
  };

  /**
   * Show more info popup
   * @param {Event} e - The click event
   * @author Sejal
   */
  const handleDrafts = async (e) => {
    e.stopPropagation();
    dispatch(setEmailType("draft"));
    const response = await dispatch(getWorkflowEmails({ workflowId: dataItem?.WorkflowId }));
    const updatedDataItem = {
      ...dataItem,
      senderNameDraft: response?.[0]?.WorkflowName || "",
      senderDraft: response?.[0]?.EmailId || "",
    };

    dispatch(setSelectedEmailData(updatedDataItem));
    dispatch(toggleDescPopup({ status: false, type: "" }));
    dispatch(setShowEmailDescPopup(true));
  };

  return (
    <td>
      <div className={`d-flex justify-space-between email-action-container ml-2 align-items-start`}>
        <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
          {dataItem?.EmailType !== email_label.DRAFT_CONSTANT && (
            <span className={`${dataItem.id === hover?.id ? "show" : "hide"} ml-1 cursor-pointer`} title={label.REPLY} onClick={handleDrafts}>
              {email_icon.REPLY}
            </span>
          )}

          <span
            ref={anchor}
            className={`${dataItem.id === hover?.id ? "show" : "hide"} ml-1 cursor-pointer`}
            title={email_label.MORE_INFO}
            onClick={handleMoreInfo}>
            {email_icon.MORE_INFO}
          </span>

      {dataItem?.EmailType !== email_label.COMPOSE_EMAIL_CONSTANT && dataItem?.EmailType !== email_label.OUTGOING_EMAIL_CONSTANT &&  <span
            className={`${dataItem.id === hover?.id ? "show" : "hide"} ml-1 cursor-pointer`}
            title={isRead ? "Mark as Unread" : "Mark as Read"}
            onClick={handleMarkAsReadUnread}>
            {isRead ? icon.MAIL_OPEN_ICON : icon.MAIL_ICON}
          </span>}

          {showLinkPopUp && (
            <EmailPopUpSidebar
              anchor={anchor}
              showPopup={showLinkPopUp}
              setShowPopup={setShowLinkPopUp}
              handleSpam={handleMarkAsSpam}
              handleDelete={handleDeleteEmails}
              EmailType={EmailType}
            />
          )}
        </Tooltip>
      </div>
    </td>
  );
};

/**
 * Component to render a custom row in the email grid
 * @param {Object} props - Component props
 * @param {Object} props.originalProps - Original props from the grid
 * @param {Object} props.tr - Table row element
 * @param {Function} props.setHover - Function to set hover state
 * @param {Function} props.exitEdit - Function to exit edit mode
 * @param {Function} props.setSelectedEmailId - Function to set selected email ID
 * @param {string} props.selectedEmailId - Selected email ID
 * @param {Function} props.setShowEmailDescPopup - Function to show email description popup
 * @param {Function} props.setSelectedEmailData - Function to set selected email data
 * @author Sejal
 */
export const RowRender = (props) => {
  const { dataItem } = props.originalProps;
  const id = store.getState()?.taskSidebar?.emailData?.id;
  const emailType = store.getState()?.taskSidebar?.emailType;
  const isRead = dataItem.IsRead === 1 || dataItem.EmailType == email_label.DRAFT_CONSTANT;
  const dispatch = useDispatch();
  const isSelected = id ? id === dataItem.id : false;
  const isHover = props?.hover?.id == dataItem.id;
  const rowStyle = isHover ? "hover-row" : isSelected && emailType !== label.DRAFT_CONSTANT ? "selected-row" : isRead ? "" : "read-row";
  const trProps = {
    ...props?.tr?.props,
    onBlur: () => props.exitEdit(),
    onMouseEnter: () => props.setHover({ id: dataItem?.id }),
    onMouseLeave: () => props.setHover({ id: number.ZERO }),
    onClick: () => {
      dispatch(setShowEmailDescPopup(true));
      dispatch(setSelectedEmailData(dataItem));
      dispatch(toggleDescPopup({ status: false, type: "" }));
      dispatch(setEmailType(""));
      props.setSelectedEmailId(dataItem);
      props.setShowEmailDescPopup(true);
      props.setSelectedEmailData(dataItem);
    },
    className: `  ${rowStyle} ${isSelected && emailType !== label.DRAFT_CONSTANT ? "active-bg  " : {}}`,
  };
  return (
    <>
      {React.cloneElement(
        props.tr,
        {
          ...trProps,
          style: {
            position: "relative",
            maxHeight: "70px",
          },
        },
        <>
          <td>{props?.tr?.props?.children[number.ZERO]}</td>
          <td className='name-field-cms'>{props?.tr?.props?.children[number.ONE]}</td>
          <td>{props?.tr?.props?.children[number.TWO]}</td>
          {!isHover && <td>{props?.tr?.props?.children[number.THREE]}</td>}
          {isHover && emailAction({ dataItem, setSelectedEmailId: props?.setSelectedEmailId })}
        </>
      )}
    </>
  );
};
